import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  useAuth
} from '../context/AuthContext';
import {
  useTheme
} from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useNotifications } from '../context/NotificationContext';
import { KOT_EVENTS, ORDER_EVENTS, NOTIFICATION_EVENTS } from '../constants/socketEvents';
import api from '../api/api';
import toast from 'react-hot-toast';
import { useToast } from '../context/ToastContext';
import KOTPrintModal from '../components/KOTPrintModal';
import NotificationBell from '../components/NotificationBell';
import AutoPrintNotification from '../components/AutoPrintNotification';
import DateFilterDropdown from '../components/DateFilterDropdown';
import {
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { mapStatusToBackend, mapStatusFromBackend } from '../utils/statusMapping';
import SpiceLevelBadge from '../components/SpiceLevelBadge';
import { formatForKitchen } from '../utils/orderFormatter';
import WindowsScrollArrows from '../components/WindowsScrollArrows';
import { CURRENCY } from '../config/currency';
import { ALLERGEN_LABELS } from '../config/allergens';
import { DietaryDot } from '../components/DietaryDot';

interface Order {
  id: number;
  customOrderId?: string;
  status: string;
  createdAt: string;
  total?: number;
  user?: { username: string } | null;
  guestName?: string;
  guestPhone?: string;
  orderNotes?: string;
  allergyNote?: string;
  /** Declared allergen enum names keyed by menuItemId (only items that declare any) */
  itemAllergens?: Record<string, string[]>;
  /** Dietary marker (VEG/NON_VEG/VEGAN) keyed by menuItemId (only declared items) */
  itemDietary?: Record<string, string>;
  items: Array<{
    id: number;
    quantity: number;
    menuItemId?: number;
    menuItemName?: string;
    menuItem: { name: string; price?: number };
    comment?: string;
    price: number;
    portionName?: string;
    spiceLevel?: string;
    toppings?: Array<{ name: string; price?: number }>;
  }>;
}

interface KitchenOrder {
  order: Order;
  kotId: number;
  kotNumber: string;
  kitchenName: string;
  kotStatus: string;
  tableInfo: string;
  qrLocation: string;
  minutesAgo: number;
  timeAgo: string;
  urgency: string;
  total: number;
  itemCount: number;
  printCount?: number;
  lastPrintedAt?: string;
}

interface KitchenStats {
  pendingCount: number;
  acceptedCount: number;
  preparingCount: number;
  readyCount: number;
  totalActiveCount: number;
  avgPrepTimeMinutes: number;
  completedTodayCount: number;
}

interface Kitchen {
  id: number;
  name: string;
  kitchenType: string;
  isDefault: boolean;
  isActive: boolean;
}

// Item style helper for modernDashboard theme - soft/subtle colors
const getItemStyle = (itemName: string) => {
  const name = itemName.toLowerCase();
  const styles: Record<string, { emoji: string; color: string; gradient: string }> = {
    // Beverages - soft tones
    coffee: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    tea: { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
    chai: { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
    juice: { emoji: '🧃', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    smoothie: { emoji: '🥤', color: '#A88898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
    water: { emoji: '💧', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #DBEAFE 100%)' },
    soda: { emoji: '🥤', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    // Fast food - soft tones
    burger: { emoji: '🍔', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    pizza: { emoji: '🍕', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    pasta: { emoji: '🍝', color: '#B07878', gradient: 'linear-gradient(135deg, #FEF2F2 0%, #FDDEDE 100%)' },
    noodle: { emoji: '🍜', color: '#A89868', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    // Rice & Indian - soft earthy tones
    rice: { emoji: '🍚', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
    biryani: { emoji: '🍛', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    curry: { emoji: '🍛', color: '#B07858', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    chicken: { emoji: '🍗', color: '#B8936B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    fish: { emoji: '🐟', color: '#6B9EB8', gradient: 'linear-gradient(135deg, #F0F9FF 0%, #DBEAFE 100%)' },
    salad: { emoji: '🥗', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
    soup: { emoji: '🍲', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    sandwich: { emoji: '🥪', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    wrap: { emoji: '🌯', color: '#6B9B7A', gradient: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)' },
    fries: { emoji: '🍟', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    // Desserts - soft pink
    cake: { emoji: '🍰', color: '#A87898', gradient: 'linear-gradient(135deg, #FDF2F8 0%, #FCE7F3 100%)' },
    ice: { emoji: '🍨', color: '#6B98A8', gradient: 'linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)' },
    dessert: { emoji: '🍮', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    // Indian breads - soft tones
    dosa: { emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    idli: { emoji: '⚪', color: '#6B7280', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)' },
    paratha: { emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    roti: { emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    naan: { emoji: '🫓', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    // Other
    egg: { emoji: '🍳', color: '#A89B68', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    paneer: { emoji: '🧀', color: '#B89B68', gradient: 'linear-gradient(135deg, #FEFCE8 0%, #FEF3C7 100%)' },
    dal: { emoji: '🥘', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
    drink: { emoji: '🥤', color: '#6B98A8', gradient: 'linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)' },
    beverage: { emoji: '🥤', color: '#6B98A8', gradient: 'linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%)' },
    meal: { emoji: '🍽️', color: '#6B7280', gradient: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)' },
    combo: { emoji: '🍱', color: '#9B8AB8', gradient: 'linear-gradient(135deg, #FAF5FF 0%, #F3E8FF 100%)' },
    thali: { emoji: '🍱', color: '#B08A6B', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEECD6 100%)' },
  };
  for (const [keyword, style] of Object.entries(styles)) {
    if (name.includes(keyword)) return style;
  }
  return { emoji: '🍽️', color: '#6B7280', gradient: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)' };
};

const KitchenDisplayPage: React.FC = () => {
  const { user } = useAuth();
  const { theme, currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard' || currentThemeName === 'scandinavianWarm';
  const socket = useSocket();
  const { showToast } = useToast();
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  // Quick menu state
  const [markAsOpenForKotId, setMarkAsOpenForKotId] = useState<number | null>(null);
  const [markAsPlacement, setMarkAsPlacement] = useState<'above' | 'below'>('below');
  const [markAsMaxHeight, setMarkAsMaxHeight] = useState<number>(256);
  const [markAsMenuLeft, setMarkAsMenuLeft] = useState<number>(8);
  const [markAsMenuTop, setMarkAsMenuTop] = useState<number>(8);
  const [markAsMenuWidth, setMarkAsMenuWidth] = useState<number>(Math.min(300, window.innerWidth - 32));
  const [markAsCols, setMarkAsCols] = useState<number>(2);
  const [markAsAlignRight, setMarkAsAlignRight] = useState<boolean>(false);
  const markAsMenuRef = useRef<HTMLDivElement | null>(null);
  const markAsTriggerRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const { addNotification } = useNotifications();
  
  const [kitchenOrders, setKitchenOrders] = useState<KitchenOrder[]>([]);
  const [stats, setStats] = useState<KitchenStats | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize] = useState<number>(20);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);

  // Status counts from backend
  const [actualStatusCounts, setActualStatusCounts] = useState<{[key: string]: number}>({});
  const [loading, setLoading] = useState<boolean>(true);
  const hasLoadedOnce = useRef(false);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<KitchenOrder | null>(null);
  const [showOrderModal, setShowOrderModal] = useState<boolean>(false);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);
  const [showKOTModal, setShowKOTModal] = useState<boolean>(false);
  const [kotOrderId, setKotOrderId] = useState<number | null>(null);
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [selectedKitchenId, setSelectedKitchenId] = useState<number | null>(null);
  const [shouldAutoPrint, setShouldAutoPrint] = useState<boolean>(false);
  const [newKotIds, setNewKotIds] = useState<Set<number>>(new Set());
  const [autoPrintNotification, setAutoPrintNotification] = useState<{
    kotNumber: string;
    tableInfo: string;
    itemCount: number;
  } | null>(null);
  const [recentlyPrintedKOTs, setRecentlyPrintedKOTs] = useState<Set<string>>(new Set());
  const [isStatsMinimized, setIsStatsMinimized] = useState<boolean>(true);
  const [statusFilter, setStatusFilter] = useState<string | null>('ACTIVE'); // Default to show all active orders (excluding SERVED)
  const [dateFilter, setDateFilter] = useState<string>('today'); // Default to today
  const [focusedKotId, setFocusedKotId] = useState<number | null>(null); // Filter to show only specific KOT

  // Check if user is a steward
  const isSteward = user?.roles?.some(role => role === 'ROLE_STEWARD');

  // Get badge style for status filter
  const getBadgeStyle = useCallback((status: string) => {
    const isActive = status === 'ALL' ? statusFilter === null :
                     status === 'ACTIVE' ? statusFilter === 'ACTIVE' :
                     statusFilter === status;

    // Modern/themed colors - use theme status references with fallbacks
    const ts = theme.components?.status;
    const modernColors: Record<string, { bg: string; activeBg: string; text: string; border: string }> = {
      ALL: { bg: '#DBEAFE', activeBg: theme.primary || '#3B82F6', text: theme.primary || '#2563EB', border: theme.primary || '#3B82F6' },
      ACTIVE: { bg: '#F1F5F9', activeBg: '#475569', text: '#475569', border: '#475569' }, /* basil-todo */
      PENDING: { bg: ts?.pending?.bg || '#FEF3C7', activeBg: ts?.pending?.text || '#F59E0B', text: ts?.pending?.text || '#D97706', border: ts?.pending?.text || '#F59E0B' },
      ACKNOWLEDGED: { bg: ts?.accepted?.bg || '#DBEAFE', activeBg: ts?.accepted?.text || '#3B82F6', text: ts?.accepted?.text || '#2563EB', border: ts?.accepted?.text || '#3B82F6' },
      PREPARING: { bg: ts?.preparing?.bg || '#F3E8FF', activeBg: ts?.preparing?.text || '#A855F7', text: ts?.preparing?.text || '#9333EA', border: ts?.preparing?.text || '#A855F7' },
      READY: { bg: ts?.ready?.bg || '#D1FAE5', activeBg: ts?.ready?.text || '#10B981', text: ts?.ready?.text || '#059669', border: ts?.ready?.text || '#10B981' },
      SERVED: { bg: ts?.served?.bg || '#E5E7EB', activeBg: ts?.served?.text || '#6B7280', text: ts?.served?.text || '#4B5563', border: ts?.served?.text || '#6B7280' },
    };

    const colors = modernColors[status] || modernColors.SERVED;
    
    if (isModernTheme) {
      return {
        fontWeight: 700 as const,
        backgroundColor: isActive ? colors.activeBg : colors.bg,
        color: isActive ? '#FFFFFF' : colors.text,
        border: `2px solid ${isActive ? colors.border : 'transparent'}`,
        boxShadow: isActive ? `0 2px 8px ${colors.border}40` : 'none',
        borderRadius: '9999px',
        padding: '6px 16px',
        transition: 'all 0.2s ease',
      };
    }
    
    // Original theme styling
    const baseStyle = {
      fontWeight: 600 as const,
    };
    
    if (status === 'ALL') {
      return {
        ...baseStyle,
        backgroundColor: statusFilter === null ? `${theme.primary}33` : `${theme.gray?.[200] || '#E5E7EB'}`,
        color: statusFilter === null ? theme.primary : theme.gray?.[600] || '#6B7280',
        border: statusFilter === null ? `1px solid ${theme.primary}50` : '1px solid transparent',
      };
    }

    if (status === 'ACTIVE') {
      return {
        ...baseStyle,
        backgroundColor: isActive ? '#47556933' : `${theme.gray?.[200] || '#E5E7EB'}`,
        color: isActive ? '#475569' : theme.gray?.[600] || '#6B7280',
        border: isActive ? '1px solid #47556950' : '1px solid transparent',
      };
    }

    if (status === 'PENDING') {
      const c = ts?.pending?.text || '#F59E0B';
      return {
        ...baseStyle,
        backgroundColor: isActive ? `${c}33` : `${theme.gray?.[200] || '#E5E7EB'}`,
        color: isActive ? c : theme.gray?.[600] || '#6B7280',
        border: isActive ? `1px solid ${c}50` : '1px solid transparent',
      };
    }
    if (status === 'ACKNOWLEDGED') {
      const c = ts?.accepted?.text || '#3B82F6';
      return {
        ...baseStyle,
        backgroundColor: isActive ? `${c}33` : `${theme.gray?.[200] || '#E5E7EB'}`,
        color: isActive ? c : theme.gray?.[600] || '#6B7280',
        border: isActive ? `1px solid ${c}50` : '1px solid transparent',
      };
    }
    if (status === 'PREPARING') {
      const c = ts?.preparing?.text || '#F59E0B';
      return {
        ...baseStyle,
        backgroundColor: isActive ? `${c}33` : `${theme.gray?.[200] || '#E5E7EB'}`,
        color: isActive ? c : theme.gray?.[600] || '#6B7280',
        border: isActive ? `1px solid ${c}50` : '1px solid transparent',
      };
    }
    if (status === 'READY') {
      const c = ts?.ready?.text || '#10B981';
      return {
        ...baseStyle,
        backgroundColor: isActive ? `${c}33` : `${theme.gray?.[200] || '#E5E7EB'}`,
        color: isActive ? c : theme.gray?.[600] || '#6B7280',
        border: isActive ? `1px solid ${c}50` : '1px solid transparent',
      };
    }
    if (status === 'SERVED') {
      const c = ts?.served?.text || '#6B7280';
      return {
        ...baseStyle,
        backgroundColor: isActive ? `${c}33` : `${theme.gray?.[200] || '#E5E7EB'}`,
        color: isActive ? c : theme.gray?.[600] || '#6B7280',
        border: isActive ? `1px solid ${c}50` : '1px solid transparent',
      };
    }
    
    return {
      ...baseStyle,
      backgroundColor: `${theme.gray?.[200] || '#E5E7EB'}`,
      color: theme.gray?.[600] || '#6B7280',
      border: '1px solid transparent',
    };
  }, [theme, statusFilter, isModernTheme]);

  // Use actual status counts from backend or calculate from current page
  const statusCounts = useMemo(() => {
    // If we have actual counts from backend, use those
    if (Object.keys(actualStatusCounts).length > 0) {
      const pending = actualStatusCounts.pending || 0;
      const acknowledged = actualStatusCounts.acknowledged || 0;
      const preparing = actualStatusCounts.preparing || 0;
      const ready = actualStatusCounts.ready || 0;
      const served = actualStatusCounts.served || 0;
      return {
        all: actualStatusCounts.all || totalElements || 0,
        active: pending + acknowledged + preparing + ready, // All except served
        pending,
        acknowledged,
        preparing,
        ready,
        served
      };
    }

    // Fallback to calculating from current page (less accurate)
    const counts = {
      all: totalElements || kitchenOrders.length,
      active: 0,
      pending: 0,
      acknowledged: 0,
      preparing: 0,
      ready: 0,
      served: 0
    };

    kitchenOrders.forEach(order => {
      const status = order.kotStatus?.toUpperCase();
      switch(status) {
        case 'PENDING': counts.pending++; counts.active++; break;
        case 'ACKNOWLEDGED': counts.acknowledged++; counts.active++; break;
        case 'PREPARING': counts.preparing++; counts.active++; break;
        case 'READY': counts.ready++; counts.active++; break;
        case 'SERVED': counts.served++; break;
      }
    });

    return counts;
  }, [kitchenOrders, actualStatusCounts, totalElements]);

  // OPTIMIZED: Single API call that fetches everything needed for the kitchen display
  // This replaces multiple separate calls to /kitchens, /orders, and /stats
  const fetchKitchenDisplayData = useCallback(async (page: number = 0, kitchenIdOverride?: number | null) => {
    try {
      // Only show full loading screen on initial load
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      setIsFetching(true);
      const params = new URLSearchParams();
      params.append('dateFilter', dateFilter);
      params.append('page', page.toString());
      params.append('size', pageSize.toString());

      // Use override if provided (for initial load), otherwise use selected
      const effectiveKitchenId = kitchenIdOverride !== undefined ? kitchenIdOverride : selectedKitchenId;
      if (effectiveKitchenId) {
        params.append('kitchenId', effectiveKitchenId.toString());
      }

      // Handle status filter - 'ACTIVE' means all except SERVED
      if (statusFilter === 'ACTIVE') {
        // Send all active statuses (excluding SERVED)
        params.append('statuses', 'PENDING,ACKNOWLEDGED,PREPARING,READY');
      } else if (statusFilter) {
        params.append('statuses', statusFilter);
      }

      // Sort by oldest first for kitchen workflow (FIFO)
      params.append('sortDirection', 'ASC');

      const response = await api.get(`/api/kitchen/display-data?${params.toString()}`);
      const data = response.data;

      // Update kitchens list (only on initial load or if empty)
      if (data.kitchens && (kitchens.length === 0 || kitchenIdOverride === null)) {
        setKitchens(data.kitchens);
      }

      // Update selected kitchen (from backend auto-selection)
      if (data.selectedKitchenId && !selectedKitchenId) {
        // Mark this kitchen ID so the useEffect doesn't trigger a duplicate fetch
        initialKitchenIdSet.current = data.selectedKitchenId;
        setSelectedKitchenId(data.selectedKitchenId);
      }

      // Update status counts
      if (data.statusCounts) {
        setActualStatusCounts(data.statusCounts);
      }

      // Update orders
      if (data.orders) {
        setKitchenOrders(data.orders.content || []);
        setCurrentPage(data.orders.page || 0);
        setTotalElements(data.orders.totalElements || 0);
        setTotalPages(data.orders.totalPages || 0);
      }

      setLoading(false);
      hasLoadedOnce.current = true;
      setIsFetching(false);
    } catch (error) {
      console.error('Error fetching kitchen display data:', error);
      toast.error('Failed to load kitchen data');
      setLoading(false);
      setIsFetching(false);
    }
  }, [selectedKitchenId, statusFilter, dateFilter, pageSize, kitchens.length]);

  // Legacy functions kept for compatibility with socket refresh events
  const fetchKitchenOrders = useCallback(async (page: number = 0) => {
    // Just call the optimized function
    await fetchKitchenDisplayData(page);
  }, [fetchKitchenDisplayData]);

  const fetchStatusCounts = useCallback(async () => {
    // Status counts are now fetched as part of display-data
    // This is kept for compatibility but does nothing extra
  }, []);

  // Initial load is handled by the filter change useEffect below

  // Handle kotId URL parameter - auto-select kitchen for specific KOT
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const kotId = params.get('kotId');

    if (kotId && kitchens.length > 0) {
      // Fetch KOT details to get the kitchen ID
      api.get(`/api/kot/${kotId}`)
        .then(response => {
          const kitchenId = response.data?.kitchenId;
          if (kitchenId) {
            console.log(`[KitchenDisplay] Auto-selecting kitchen ${kitchenId} for KOT ${kotId}`);
            console.log(`[KitchenDisplay] Setting filters - kitchen: ${kitchenId}, status: null, date: all, focused KOT: ${kotId}`);
            setSelectedKitchenId(kitchenId);
            // Clear status filter to show all orders
            setStatusFilter(null);
            // Set date filter to 'all' to ensure KOT is visible regardless of date
            setDateFilter('all');
            // Set focused KOT to show only this specific KOT
            setFocusedKotId(parseInt(kotId));
            console.log('[KitchenDisplay] Filters updated successfully');
            // Scroll to the KOT card after a short delay to let it render
            setTimeout(() => {
              const kotCard = document.querySelector(`[data-kot-id="${kotId}"]`);
              if (kotCard) {
                kotCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                // Add a temporary highlight effect
                kotCard.classList.add('ring-4', 'ring-blue-500', 'ring-opacity-50');
                setTimeout(() => {
                  kotCard.classList.remove('ring-4', 'ring-blue-500', 'ring-opacity-50');
                }, 3000);
              }
            }, 500);
          }
        })
        .catch(error => {
          console.error('[KitchenDisplay] Error fetching KOT details:', error);
        });
    }
  }, [kitchens]);

  // Refresh function that updates both orders and stats
  const refreshKitchenData = useCallback(() => {
    fetchKitchenOrders();
    fetchStatusCounts(); // OPTIMIZED: This fetches both stats and counts
  }, [fetchKitchenOrders, fetchStatusCounts]);

  // Define openKOTModal early so it can be used in effects
  const openKOTModal = useCallback((orderId: number, autoPrint: boolean = false) => {
    // Ensure any open quick menu is closed before opening the print modal
    setMarkAsOpenForKotId(null);
    setKotOrderId(orderId);
    setShouldAutoPrint(autoPrint);
    setShowKOTModal(true);
  }, []);

  // Handle automatic KOT printing without modal
  const handleAutoPrintKOT = useCallback(async (orderId: number, kotNumber: string, tableInfo: string) => {
    // Check if this KOT was recently printed (within last 10 seconds)
    const printKey = `${orderId}-${kotNumber}`;
    if (recentlyPrintedKOTs.has(printKey)) {
      console.log('[KitchenDisplay] KOT already printed recently, skipping:', printKey);
      return;
    }

    try {
      // Add to recently printed set
      setRecentlyPrintedKOTs(prev => new Set([...prev, printKey]));
      
      // Remove from recently printed after 10 seconds
      setTimeout(() => {
        setRecentlyPrintedKOTs(prev => {
          const newSet = new Set(prev);
          newSet.delete(printKey);
          return newSet;
        });
      }, 10000);

      // Fetch KOT data
      const response = await api.get(`/api/kitchen/orders/${orderId}/kot-template`);
      const kotData = response.data;
      
      // Show auto-print notification
      setAutoPrintNotification({
        kotNumber: kotNumber || kotData.kotNumber,
        tableInfo: tableInfo || kotData.tableInfo,
        itemCount: kotData.totalQuantity || kotData.items?.length || 0
      });
      
      // Generate print content
      const printContent = generatePrintableKOT(kotData);
      
      // Try to print using iframe (no popup blocker issues)
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.top = '-10000px';
      iframe.style.left = '-10000px';
      iframe.style.width = '1px';
      iframe.style.height = '1px';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(printContent);
        iframeDoc.close();
        
        iframe.onload = () => {
          setTimeout(() => {
            try {
              iframe.contentWindow?.focus();
              iframe.contentWindow?.print();
              
              // Mark as printed after a delay
              setTimeout(async () => {
                if (kotData?.kotNumber) {
                  try {
                    const kotIdMatch = kotData.kotNumber.match(/KOT-(\d+)-/);
                    if (kotIdMatch && kotIdMatch[1]) {
                      await api.put(`/api/kot/${kotIdMatch[1]}/status`, { status: 'PRINTED' });
                      console.log('[KitchenDisplay] Marked KOT as auto-printed');
                    }
                  } catch (error) {
                    console.error('[KitchenDisplay] Failed to mark KOT as printed:', error);
                  }
                }
                
                // Clean up iframe
                document.body.removeChild(iframe);
              }, 1000);
            } catch (error) {
              console.error('Auto-print error:', error);
              document.body.removeChild(iframe);
            }
          }, 500);
        };
      }
    } catch (error) {
      console.error('[KitchenDisplay] Auto-print failed:', error);
      toast.error('Failed to auto-print KOT');
    }
  }, [recentlyPrintedKOTs]);

  // Helper function to generate printable KOT (copied from KOTPrintModal)
  const generatePrintableKOT = (data: any): string => {
    const formatTime = (timeStr: string) => {
      return new Date(timeStr).toLocaleString();
    };

    const getPriorityColor = (priority: string) => {
      switch (priority) {
        case 'HIGH': return '#ff4444';
        case 'MEDIUM': return '#ff8800';
        default: return '#00aa00';
      }
    };

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Kitchen Order Ticket - ${data.kotNumber}</title>
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
              @page { margin: 0; }
            }
            
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              line-height: 1.4;
              margin: 0;
              padding: 10px;
              max-width: 300px;
            }
            
            .kot-header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 10px;
            }
            
            .kot-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .priority {
              font-weight: bold;
              padding: 2px 6px;
              border-radius: 3px;
              color: white;
              background-color: ${getPriorityColor(data.priority)};
            }
            
            .info-section {
              margin-bottom: 10px;
              border-bottom: 1px dashed #666;
              padding-bottom: 8px;
            }
            
            .info-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 3px;
            }
            
            .items-section {
              margin-bottom: 10px;
            }
            
            .item {
              margin-bottom: 8px;
              border-bottom: 1px dotted #999;
              padding-bottom: 5px;
            }
            
            .item-header {
              font-weight: bold;
              font-size: 14px;
            }
            
            .item-qty {
              float: right;
              font-weight: bold;
            }
            
            .instructions {
              font-style: italic;
              color: #666;
              margin-top: 3px;
              font-size: 11px;
            }
            
            .footer {
              text-align: center;
              border-top: 2px solid #000;
              padding-top: 10px;
              margin-top: 10px;
            }
            
            .total-summary {
              font-weight: bold;
              font-size: 14px;
            }
          </style>
        </head>
        <body>
          <div class="kot-header">
            <div class="kot-title">KITCHEN ORDER TICKET</div>
            <div>KOT: ${data.kotNumber}</div>
            <div class="priority">${data.priority} PRIORITY</div>
          </div>
          
          <div class="info-section">
            <div class="info-row">
              <span><strong>Order #:</strong> ${data.orderNumber}</span>
              <span><strong>Wait:</strong> ${data.waitTime}min</span>
            </div>
            <div class="info-row">
              <span><strong>Table:</strong> ${data.tableInfo}</span>
              <span><strong>Type:</strong> ${data.deliveryType}</span>
            </div>
            <div class="info-row">
              <span><strong>Customer:</strong> ${data.customerName}</span>
            </div>
            <div class="info-row">
              <span><strong>Time:</strong> ${formatTime(data.orderTime)}</span>
            </div>
          </div>

          ${data.allergyNote ? `
          <div style="border: 3px solid #000; padding: 8px; margin-bottom: 10px; font-weight: bold;">
            *** ALLERGY — READ BEFORE PREPARING ***<br>
            ${data.allergyNote}
          </div>
          ` : ''}

          <div class="items-section">
            <div style="font-weight: bold; margin-bottom: 8px;">ITEMS TO PREPARE:</div>
            ${data.items.map((item: any) => `
              <div class="item">
                <div class="item-header">
                  ${item.itemName}
                  <span class="item-qty">x${item.quantity}</span>
                </div>
                ${item.instructions.length > 0 ? `
                  <div class="instructions">
                    ${item.instructions.join('<br>')}
                  </div>
                ` : ''}
              </div>
            `).join('')}
          </div>
          
          <div class="footer">
            <div class="total-summary">
              Total Items: ${data.totalQuantity} | Amount: ${CURRENCY.symbol}${data.orderTotal.toFixed(2)}
            </div>
            <div style="margin-top: 5px; font-size: 10px;">
              Printed: ${formatTime(data.printTime)}
            </div>
          </div>
        </body>
      </html>
    `;
  };

  // Track if initial data has been loaded
  const initialLoadDone = useRef(false);
  // Track the kitchen ID that was set from the initial API response (to avoid duplicate fetch)
  const initialKitchenIdSet = useRef<number | null>(null);

  // Load orders on initial mount and when filters change
  useEffect(() => {
    // On initial load, fetch with null to let backend auto-select default kitchen
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchKitchenDisplayData(0, null);
      return;
    }

    // Skip the fetch if this is the kitchen ID that was just set from the initial API response
    // This prevents duplicate API calls when the backend auto-selects a kitchen
    if (initialKitchenIdSet.current === selectedKitchenId) {
      initialKitchenIdSet.current = null; // Reset so future changes trigger fetches
      return;
    }

    // On subsequent filter changes
    if (selectedKitchenId) {
      // Persist selected kitchen globally
      try { localStorage.setItem('selectedKitchenId', String(selectedKitchenId)); } catch {}
      // Fetch with current filters
      fetchKitchenDisplayData(0, selectedKitchenId);
    } else if (kitchens.length > 0) {
      try { localStorage.removeItem('selectedKitchenId'); } catch {}
      setKitchenOrders([]);
      setActualStatusCounts({});
      setLoading(false);
    }
  }, [selectedKitchenId, statusFilter, dateFilter]);


  // Initialize Socket.IO connection
  useEffect(() => {
    if (!user) return;

    if (socket) {
      setRealtimeConnected(socket.isConnected);
      console.log('[KitchenDisplay] Socket.IO connection status:', socket.isConnected);
    }
  }, [user, socket]);

  // Subscribe to Socket.IO events
  useEffect(() => {
    if (!socket || !user || !selectedKitchenId) {
      console.log('[KitchenDisplay] Socket not connected or no kitchen selected');
      return;
    }

    console.log('[KitchenDisplay] Subscribing to Socket.IO events for kitchen:', selectedKitchenId);

    // Track processed KOT IDs to prevent duplicates within this effect lifecycle
    const processedKots = new Set<string>();

    // Subscribe to kitchen-specific updates
    socket.subscribeToKitchen(selectedKitchenId);
    
    const handleKotCreated = (kot: any) => {
        // Create a unique key for this KOT
        const kotKey = `${kot?.kotNumber}-${kot?.orderId}`;
        
        // Skip if we've already processed this KOT in this session
        if (processedKots.has(kotKey)) {
            console.log('[KitchenDisplay] Skipping duplicate KOT event:', kotKey);
            return;
        }
        processedKots.add(kotKey);
        
        console.log('[KitchenDisplay] Socket.IO kotCreated event received:', kot);
        
        const notificationKitchenId = kot?.kitchenId;
        const orderId = kot?.orderId;
        const kotId = kot?.id || kot?.kotId;
        const kotNumber = kot?.kotNumber;
        const tableNumber = kot?.tableNumber;
        const isPrinted = kot?.printed === 'true' || kot?.printed === true;
        const kotStatus = kot?.status || 'PENDING';
        
        console.log('[KitchenDisplay] KOT notification details:', {
          notificationKitchenId,
          selectedKitchenId,
          kotNumber,
          tableNumber,
          kotStatus,
          kitchenMatch: Number(notificationKitchenId) === Number(selectedKitchenId)
        });
        
        // Always add to notification history (bell)
        addNotification({
          type: 'order',
          title: 'New Kitchen Order',
          message: `${kotNumber || 'New KOT'} for ${tableNumber || 'Table'} - ${kot?.kitchenName || 'Kitchen'}`,
          data: kot
        });
        
        // Only process (toast + auto-print) if this KOT is for the currently selected kitchen
        if (selectedKitchenId && notificationKitchenId && 
            Number(notificationKitchenId) === Number(selectedKitchenId)) {
          
          // Check if this KOT matches current status filter
          const matchesFilter = !statusFilter || statusFilter === kotStatus;
          
          if (matchesFilter) {
            console.log('[KitchenDisplay] Showing toast for KOT:', kotNumber);
            
            // Show toast notification
            showToast({
              message: `🔔 New KOT: ${kotNumber} for ${tableNumber || 'Table'}`,
              kind: 'success',
              icon: '🔔',
              durationMs: 5000,
              idKey: `kot_new_${kotNumber}`,
              allowedPaths: ['/vendor/kitchen']
            });
            
            // Add KOT ID to new KOTs set for blinking effect
            if (kotId) {
              setNewKotIds(prev => new Set([...prev, Number(kotId)]));
              // Remove blinking effect after 5 seconds
              setTimeout(() => {
                setNewKotIds(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(Number(kotId));
                  return newSet;
                });
              }, 5000);
            }
          }
          
          // Auto-print KOT if not already printed and we have an orderId
          if (orderId && !isPrinted) {
            console.log('[KitchenDisplay] Auto-printing KOT from Socket.IO for selected kitchen:', selectedKitchenId);
            handleAutoPrintKOT(Number(orderId), kotNumber || `Order-${orderId}`, tableNumber || 'Table');
          } else {
            if (isPrinted) {
              console.log('[KitchenDisplay] KOT already printed, skipping auto-print');
            }
            if (!orderId) {
              console.log('[KitchenDisplay] No orderId in KOT notification, cannot auto-print');
            }
          }
          
          // Only refresh if the KOT matches current filters
          if (matchesFilter) {
            refreshKitchenData();
          }
        } else {
          console.log('[KitchenDisplay] KOT is for different kitchen. Current:', selectedKitchenId, 'Notification kitchen:', notificationKitchenId);
        }
    };
    
    const handleKotUpdated = (kot: any) => {
        console.log('[KitchenDisplay] Socket.IO kotUpdated event received:', kot);
        const kotStatus = kot?.status;
        // Only refresh if the updated KOT matches current filters
        const shouldRefresh = !statusFilter || statusFilter === kotStatus || statusFilter === kot?.previousStatus;
        if (shouldRefresh) {
            refreshKitchenData();
        }
    };
    
    const handleKotStatusChanged = (data: any) => {
        console.log('[KitchenDisplay] Socket.IO kotStatusChanged event received:', data);
        const newStatus = data?.status || data?.newStatus;
        const oldStatus = data?.previousStatus || data?.oldStatus;
        // Only refresh if the status change affects current filters
        const shouldRefresh = !statusFilter || statusFilter === newStatus || statusFilter === oldStatus;
        if (shouldRefresh) {
            refreshKitchenData();
        }
    };
    
    const handleNewOrder = (order: any) => {
        console.log('[KitchenDisplay] Socket.IO newOrder event received:', order);
        refreshKitchenData();
    };
    
    const handleOrderUpdate = (order: any) => {
        console.log('[KitchenDisplay] Socket.IO orderUpdate event received:', order);
        refreshKitchenData();
    };

    const handleOrderStatusUpdate = (data: any) => {
        console.log('[KitchenDisplay] Socket.IO orderStatusUpdate event received:', data);
        // When an order status changes, refresh KOT data as it may affect KOT display
        refreshKitchenData();
    };
    
    const handleNotification = (notification: any) => {
        console.log('[KitchenDisplay] Socket.IO notification received:', notification);
        const event = notification?.event || '';

        // Handle KOT creation events (standardized + legacy names)
        if (event === KOT_EVENTS.NEW || event === 'kotCreated' || event === 'KOT_CREATED') {
            console.log('[KitchenDisplay] Processing KOT from notification event');
            const kotData = notification.data || {};

            // Process as a new KOT (duplicate check will prevent double processing)
            handleKotCreated({
                ...kotData,
                kitchenId: notification.kitchenId || kotData.kitchenId,
                vendorId: notification.vendorId || kotData.vendorId
            });
            return;
        } else if (event === KOT_EVENTS.UPDATED || event === KOT_EVENTS.STATUS_UPDATE || event === 'kotStatusUpdate') {
            console.log('[KitchenDisplay] Skipping KOT update notification - handled by standardized listener');
            return;
        } else if (event === ORDER_EVENTS.NEW || event === 'NEW_ORDER') {
            // Handle new orders - only add to notification bell, no toast
            const orderData = notification.data || {};
            console.log('[KitchenDisplay] New order notification received (vendor-wide)');
            
            // Add to notification bell without toast - this is a vendor-wide notification
            addNotification({
              type: 'order',
              title: 'New Order',
              message: `Order #${orderData.orderNumber || orderData.orderId} received`,
              data: orderData
            });
            
            // Don't auto-print for NEW_ORDER as it doesn't have kitchen-specific info
            // KOT creation will trigger the kitchen-specific print
            
            refreshKitchenData();
        }
    };

    // Register Socket.IO event listeners using standardized event names
    socket.on(KOT_EVENTS.NEW, handleKotCreated);
    socket.on(KOT_EVENTS.UPDATED, handleKotUpdated);
    socket.on(KOT_EVENTS.STATUS_UPDATE, handleKotStatusChanged);
    socket.on(ORDER_EVENTS.NEW, handleNewOrder);
    socket.on(ORDER_EVENTS.UPDATED, handleOrderUpdate);
    socket.on(ORDER_EVENTS.STATUS_UPDATE, handleOrderStatusUpdate);
    socket.on(NOTIFICATION_EVENTS.GENERAL, handleNotification);

    // Subscribe to vendor-wide notifications
    socket.subscribeToVendor(user.id);

    console.log('[KitchenDisplay] Socket.IO event listeners attached (using standardized events)');

    return () => {
      console.log('[KitchenDisplay] Cleaning up Socket.IO event listeners');
      socket.off(KOT_EVENTS.NEW, handleKotCreated);
      socket.off(KOT_EVENTS.UPDATED, handleKotUpdated);
      socket.off(KOT_EVENTS.STATUS_UPDATE, handleKotStatusChanged);
      socket.off(ORDER_EVENTS.NEW, handleNewOrder);
      socket.off(ORDER_EVENTS.UPDATED, handleOrderUpdate);
      socket.off(ORDER_EVENTS.STATUS_UPDATE, handleOrderStatusUpdate);
      socket.off(NOTIFICATION_EVENTS.GENERAL, handleNotification);
      socket.unsubscribeFromKitchen(selectedKitchenId);
      socket.unsubscribeFromVendor(user.id);
    };
  }, [socket, user, selectedKitchenId, statusFilter, dateFilter, refreshKitchenData, theme, addNotification, handleAutoPrintKOT]);

  // Update KOT status
  const updateKOTStatus = async (kotId: number, newStatus: string) => {
    try {
      setUpdatingOrderId(kotId);

      // Find the kitchen order to get the order ID
      const kitchenOrder = kitchenOrders.find(ko => ko.kotId === kotId);
      if (!kitchenOrder) {
        throw new Error('Kitchen order not found');
      }

      console.log('🔄 Updating KOT status:', { kotId, newStatus, currentStatus: kitchenOrder.kotStatus, orderId: kitchenOrder.order.id });

      // Use the KOT-specific endpoint to update individual KOT status
      // This ensures only this KOT is updated, not all KOTs in the order
      // The backend will automatically sync the order status when all KOTs reach the same state
      const response = await api.put(`/api/kot/${kotId}/status`, { status: newStatus });
      
      console.log('✅ API response received:', response.data);
      console.log('📊 Order status in response:', response.data.status);
      
      // Update the local state immediately (like VendorOrders does)
      setKitchenOrders(prevOrders => {
        const updatedOrders = prevOrders.map(ko => 
          ko.kotId === kotId 
            ? { 
                ...ko, 
                order: { ...ko.order, status: newStatus },
                kotStatus: newStatus 
              }
            : ko
        );
        console.log('🔄 Local state updated for KOT:', kotId, 'new status:', newStatus);
        return updatedOrders;
      });
      
      // Also update the selected order if it's the same one
      if (selectedOrder && selectedOrder.kotId === kotId) {
        setSelectedOrder(prev => prev ? {
          ...prev,
          order: { ...prev.order, status: newStatus },
          kotStatus: newStatus
        } : null);
        console.log('🔄 Selected order updated for KOT:', kotId, 'new status:', newStatus);
      }
      
      // Close the modal after successful update
      closeOrderModal();
      
      // Refresh stats and status counts to update counters
      // Use setTimeout to prevent race conditions
      setTimeout(async () => {
        try {
          console.log('📈 Refreshing kitchen stats and status counts...');
          await fetchStatusCounts(); // OPTIMIZED: Single call fetches both stats and counts
          console.log('📈 Kitchen stats and status counts refreshed successfully');
        } catch (error) {
          console.error('Error refreshing data after status update:', error);
        }
      }, 100);
      
      // Fallback: Force refresh after a longer delay to ensure consistency
      setTimeout(async () => {
        try {
          console.log('🔄 Fallback refresh: fetching fresh data from server...');
          await fetchKitchenOrders(0);
          console.log('🔄 Fallback refresh completed');
        } catch (error) {
          console.error('Error in fallback refresh:', error);
        }
      }, 1000); // Increased delay to give server more time to process
      
      toast.success(`Order #${kitchenOrder.order.customOrderId || kitchenOrder.order.id} status updated to ${newStatus.toLowerCase()}`);
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  const openOrderModal = (kitchenOrder: KitchenOrder) => {
    setSelectedOrder(kitchenOrder);
    setShowOrderModal(true);
  };

  const closeOrderModal = () => {
    setSelectedOrder(null);
    setShowOrderModal(false);
  };

  const closeKOTModal = () => {
    setKotOrderId(null);
    setShouldAutoPrint(false);
    setShowKOTModal(false);
  };

  const getStatusColor = (status: string) => {
    const themeStatus = theme.components?.status;
    switch (status) {
      case 'PENDING':
        return {
          bg: themeStatus?.pending?.bg || '#fef3c7',
          text: themeStatus?.pending?.text || '#92400e',
          border: themeStatus?.pending?.border || '#f59e0b'
        };
      case 'ACKNOWLEDGED':
        return {
          bg: themeStatus?.accepted?.bg || '#dbeafe',
          text: themeStatus?.accepted?.text || '#1e40af',
          border: themeStatus?.accepted?.border || '#3b82f6'
        };
      case 'PREPARING':
        return {
          bg: themeStatus?.preparing?.bg || '#fde68a',
          text: themeStatus?.preparing?.text || '#92400e',
          border: themeStatus?.preparing?.border || '#f59e0b'
        };
      case 'READY':
        return {
          bg: themeStatus?.ready?.bg || '#d1fae5',
          text: themeStatus?.ready?.text || '#065f46',
          border: themeStatus?.ready?.border || '#10b981'
        };
      case 'SERVED':
        return {
          bg: themeStatus?.served?.bg || '#e5e7eb',
          text: themeStatus?.served?.text || '#374151',
          border: themeStatus?.served?.border || '#9ca3af'
        };
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#6b7280' }; // gray /* basil-todo */
    }
  };

  const getUrgencyColor = (urgency: string) => {
    const themeStatus = theme.components?.status;
    switch (urgency) {
      case 'high':
        return themeStatus?.urgent?.text || '#ef4444'; // red / terracotta
      case 'medium':
        return themeStatus?.pending?.text || '#f59e0b'; // amber
      default:
        return themeStatus?.ready?.text || '#10b981'; // green
    }
  };

  const getNextStatus = (currentStatus: string) => {
    switch (currentStatus) {
      case 'PENDING':
        return 'ACKNOWLEDGED';
      case 'ACKNOWLEDGED':
        return 'PREPARING';
      case 'PREPARING':
        return 'READY';
      case 'READY':
        return 'SERVED';
      default:
        return null;
    }
  };

  // Valid next statuses for quick menu (mirrors Orders page behavior for forward transitions)
  const getValidNextKOTStatuses = useCallback((currentStatus: string): string[] => {
    const transitions: { [key: string]: string[] } = {
      PENDING: ['ACKNOWLEDGED', 'PREPARING', 'READY', 'SERVED'],
      ACKNOWLEDGED: ['PREPARING', 'READY', 'SERVED'],
      PREPARING: ['READY', 'SERVED'],
      READY: ['SERVED'],
      SERVED: [],
    };
    return transitions[currentStatus] || [];
  }, []);

  // Recompute overlay position on open/resize/scroll for precise anchoring
  useEffect(() => {
    if (markAsOpenForKotId === null) return;
    const recompute = () => {
      const btn = markAsTriggerRefs.current[markAsOpenForKotId!];
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const bottomSafeArea = 100; // space for bottom nav / safe area
      const width = 256; // match Orders page quick menu width (w-64)
      setMarkAsMenuWidth(width);
      setMarkAsCols(2);
      // Follow Orders page behavior: align left by default, flip when near viewport right edge
      const alignRight = window.innerWidth - r.left < 320;
      setMarkAsAlignRight(alignRight);
      // Desktop/web: keep the menu inside the current card bounds so it doesn't touch adjacent cards
      const isDesktop = window.innerWidth >= 1024;
      let left: number;
      if (isDesktop) {
        const cardEl = btn.closest('[data-kot-card]') as HTMLElement | null;
        const cardRect = cardEl?.getBoundingClientRect();
        if (cardRect) {
          const minLeft = Math.max(8, cardRect.left + 8);
          const maxLeft = Math.min(window.innerWidth - width - 8, cardRect.right - width - 8);
          const desired = alignRight ? r.right - width : r.left;
          left = Math.min(Math.max(desired, minLeft), maxLeft);
        } else {
          left = alignRight
            ? Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8)
            : Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
        }
      } else {
        left = alignRight
          ? Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8)
          : Math.min(Math.max(8, r.left), window.innerWidth - width - 8);
      }
      setMarkAsMenuLeft(left);

      requestAnimationFrame(() => {
        const el = markAsMenuRef.current;
        const naturalHeight = el?.scrollHeight || 220;
        const maxHeight = Math.min(320, Math.max(160, naturalHeight));
        const spaceBelow = window.innerHeight - bottomSafeArea - r.bottom - 8; // gap
        const spaceAbove = r.top - 8;

        let finalPlace: 'above' | 'below';
        let top: number;
        if (spaceBelow >= maxHeight) {
          finalPlace = 'below';
          top = r.bottom + 8;
        } else if (spaceAbove >= maxHeight) {
          finalPlace = 'above';
          top = r.top - maxHeight - 8;
        } else if (spaceBelow >= spaceAbove) {
          finalPlace = 'below';
          top = Math.min(r.bottom + 8, window.innerHeight - bottomSafeArea - maxHeight - 8);
        } else {
          finalPlace = 'above';
          top = Math.max(8, r.top - maxHeight - 8);
        }
        setMarkAsPlacement(finalPlace);
        setMarkAsMaxHeight(maxHeight);
        setMarkAsMenuTop(top);
      });
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [markAsOpenForKotId]);

  // Close the quick menu on outside click or Escape
  useEffect(() => {
    if (markAsOpenForKotId === null) return;

    const handlePointerDown = (e: MouseEvent) => {
      const menuEl = markAsMenuRef.current;
      const triggerEl =
        markAsOpenForKotId != null ? markAsTriggerRefs.current[markAsOpenForKotId] : null;
      const target = e.target as Node | null;
      if (!target) return;
      if (menuEl && menuEl.contains(target)) return;
      if (triggerEl && triggerEl.contains && triggerEl.contains(target as Node)) return;
      setMarkAsOpenForKotId(null);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMarkAsOpenForKotId(null);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [markAsOpenForKotId]);

  if (loading && !hasLoadedOnce.current) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: theme.background }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: theme.primary }}></div>
          <p style={{ color: theme.text }}>Loading kitchen display...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-3 md:p-6" style={{ backgroundColor: theme.background }}>
      <style>{`
        .filter-scroll {
          overflow-x: auto;
          scrollbar-width: thin;
          scrollbar-color: ${theme.gray?.[300] || '#D1D5DB'} transparent;
        }
        .filter-scroll::-webkit-scrollbar {
          height: 3px;
        }
        .filter-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .filter-scroll::-webkit-scrollbar-thumb {
          background: ${theme.gray?.[300] || '#D1D5DB'};
          border-radius: 3px;
        }
        .filter-scroll::-webkit-scrollbar-thumb:hover {
          background: ${theme.gray?.[400] || '#9CA3AF'};
        }
        
        @keyframes glow {
          from {
            border-color: ${theme.primary};
            box-shadow: 0 0 10px ${theme.primary}, 0 0 20px ${theme.primary}, 0 0 30px ${theme.primary};
          }
          to {
            border-color: ${theme.success};
            box-shadow: 0 0 20px ${theme.success}, 0 0 30px ${theme.success}, 0 0 40px ${theme.success};
          }
        }
        
        .animate-pulse-border {
          animation: pulse-border 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse-border {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.02);
          }
        }
      `}</style>
      {/* Toaster provided globally via ToastProvider */}
      
      {/* Header */}
      <div className="mb-2 md:mb-6">
        {/* Desktop Layout */}
        <div className="hidden md:flex md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold" style={{ color: theme.text }}>
              Kitchen
            </h1>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Date Filter Dropdown - moved here for mobile */}
            <DateFilterDropdown
              value={dateFilter}
              onChange={setDateFilter}
              showAllOption={true}
            />
            
            {/* Notification Bell */}
            <NotificationBell />
            
            
            {/* Kitchen Selector - Only show if kitchens exist */}
            {kitchens.length > 0 && (
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ 
                minWidth: { xs: 100, sm: 200 },
                maxWidth: { xs: 140, sm: 'none' },
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.components?.card?.background || '#FFFFFF',
                  '&:hover fieldset': {
                    borderColor: theme.primary
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.primary
                  }
                },
                '& .MuiInputLabel-root': {
                  color: theme.text,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  '&.Mui-focused': {
                    color: theme.primary
                  }
                },
                '& .MuiSelect-select': {
                  color: theme.text,
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  paddingY: { xs: '8px', sm: '10px' }
                }
              }}
            >
              <InputLabel>Kitchen</InputLabel>
              <Select
                value={selectedKitchenId || ''}
                onChange={(e) => setSelectedKitchenId(e.target.value as number)}
                label="Kitchen"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxWidth: { xs: '90vw', sm: '400px' },
                      maxHeight: 300
                    }
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  }
                }}
              >
                {kitchens.map((kitchen) => (
                  <MenuItem key={kitchen.id} value={kitchen.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{kitchen.name.replace(/\s*Kitchen\s*$/i, '')}</span>
                      {kitchen.isDefault && (
                        <span 
                          className="ml-2 text-xs px-1 py-0.5 rounded-full" 
                          style={{ 
                            backgroundColor: theme.primary + '20',
                            color: theme.primary 
                          }}
                        >
                          Default
                        </span>
                      )}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            )}
          </div>
        </div>
        
        {/* Mobile Layout */}
        <div className="md:hidden">
          {/* Title row with notification and diagnostics */}
          <div className="flex items-center justify-between mb-1.5">
            <h1 className="text-lg font-semibold" style={{ color: theme.text }}>
              Kitchen
            </h1>
            <div className="flex items-center gap-2">
              {/* Notification Bell */}
              <NotificationBell />
              
            </div>
          </div>
          
          {/* Date filter and Kitchen selector row */}
          <div className="flex items-center gap-2">
            {/* Date Filter Dropdown */}
            <DateFilterDropdown
              value={dateFilter}
              onChange={setDateFilter}
              showAllOption={true}
            />
            
            {/* Kitchen Selector - Only show if kitchens exist */}
            {kitchens.length > 0 && (
            <FormControl 
              variant="outlined" 
              size="small" 
              sx={{ 
                flex: 1,
                minWidth: 100,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: theme.components?.card?.background || '#FFFFFF',
                  '&:hover fieldset': {
                    borderColor: theme.primary
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.primary
                  }
                },
                '& .MuiInputLabel-root': {
                  color: theme.text,
                  fontSize: '0.875rem',
                  '&.Mui-focused': {
                    color: theme.primary
                  }
                },
                '& .MuiSelect-select': {
                  color: theme.text,
                  fontSize: '0.875rem',
                  paddingY: '8px'
                }
              }}
            >
              <InputLabel>Kitchen</InputLabel>
              <Select
                value={selectedKitchenId || ''}
                onChange={(e) => setSelectedKitchenId(e.target.value as number)}
                label="Kitchen"
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxWidth: '90vw',
                      maxHeight: 300
                    }
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left'
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left'
                  }
                }}
              >
                {kitchens.map((kitchen) => (
                  <MenuItem key={kitchen.id} value={kitchen.id}>
                    <div className="flex items-center justify-between w-full">
                      <span>{kitchen.name.replace(/\s*Kitchen\s*$/i, '')}</span>
                      {kitchen.isDefault && (
                        <span 
                          className="ml-2 text-xs px-1 py-0.5 rounded-full" 
                          style={{ 
                            backgroundColor: theme.primary + '20',
                            color: theme.primary 
                          }}
                        >
                          Default
                        </span>
                      )}
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            )}

            {/* Show All KOTs Button - appears when a specific KOT is focused */}
            {focusedKotId !== null && (
              <button
                onClick={() => setFocusedKotId(null)}
                className="px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap"
                style={{
                  backgroundColor: theme.primary,
                  color: 'var(--text-inverse)',
                  border: 'none',
                  cursor: 'pointer'
                }}
                title="Show all KOTs"
              >
                Show All KOTs
              </button>
            )}
          </div>
        </div>
      </div>
        
        {/* Status filter tabs — clean enterprise tab bar */}
        {(() => {
          const KDS_TAB_COLORS: Record<string, string> = {
            ACTIVE: '#262C26', PENDING: '#C4956E', ACKNOWLEDGED: '#2D6CB5', PREPARING: '#A66A10',
            READY: '#3DA06A', SERVED: '#5D5347',
          };
          const tabs: { key: string | null; label: string; count: number; onClick: () => void }[] = [
            { key: 'ACTIVE', label: 'Active', count: statusCounts.active, onClick: () => setStatusFilter('ACTIVE') },
            { key: null, label: 'All', count: statusCounts.all, onClick: () => setStatusFilter(null) },
            { key: 'PENDING', label: 'Pending', count: statusCounts.pending, onClick: () => setStatusFilter(prev => (prev === 'PENDING' ? 'ACTIVE' : 'PENDING')) },
            { key: 'ACKNOWLEDGED', label: 'Acknowledged', count: statusCounts.acknowledged, onClick: () => setStatusFilter(prev => (prev === 'ACKNOWLEDGED' ? 'ACTIVE' : 'ACKNOWLEDGED')) },
            { key: 'PREPARING', label: 'Preparing', count: statusCounts.preparing, onClick: () => setStatusFilter(prev => (prev === 'PREPARING' ? 'ACTIVE' : 'PREPARING')) },
            { key: 'READY', label: 'Ready', count: statusCounts.ready, onClick: () => setStatusFilter(prev => (prev === 'READY' ? 'ACTIVE' : 'READY')) },
            { key: 'SERVED', label: 'Served', count: statusCounts.served, onClick: () => setStatusFilter(prev => (prev === 'SERVED' ? 'ACTIVE' : 'SERVED')) },
          ];
          return (
            <div className="relative mt-2 md:mt-4 mb-2 transition-opacity duration-200 collapsible-chrome" style={{ opacity: isFetching ? 0.5 : 1, pointerEvents: isFetching ? 'none' : 'auto' }}>
              {/* Segmented status bar — distributes across the full width on
                  desktop instead of huddling left; scrolls on mobile. */}
              <div className="flex items-stretch gap-1.5 overflow-x-auto no-scrollbar rounded-xl p-1.5" style={{ backgroundColor: 'var(--bg-sunken)', border: '1px solid var(--border-subtle)' }}>
                {tabs.map(tab => {
                  const isActive = tab.key === 'ACTIVE' ? statusFilter === 'ACTIVE' : tab.key === null ? statusFilter === null : statusFilter === tab.key;
                  const accent = KDS_TAB_COLORS[tab.key || 'ACTIVE'] || '#64748B';
                  return (
                    <button
                      key={tab.key ?? 'all'}
                      onClick={tab.onClick}
                      className="relative px-3 text-[12.5px] font-semibold whitespace-nowrap transition-colors rounded-lg flex items-center justify-center gap-1.5"
                      style={{
                        flex: '1 1 0',
                        minWidth: 96,
                        minHeight: 'var(--chrome-pill-h)',
                        color: isActive ? 'var(--text-inverse)' : theme.gray?.[600] || '#555',
                        backgroundColor: isActive ? accent : 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {tab.label}
                      <span
                        className="text-[10.5px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{
                          backgroundColor: isActive ? 'rgba(255,255,255,0.25)' : 'var(--bg-surface)',
                          color: isActive ? 'var(--text-inverse)' : theme.gray?.[500] || '#777',
                          border: isActive ? 'none' : '1px solid var(--border-subtle)',
                        }}
                      >
                        {tab.count}
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* Right fade — scroll hint */}
              <div className="absolute right-0 top-0 bottom-0 w-8 pointer-events-none md:hidden" style={{ background: `linear-gradient(to right, transparent, ${theme.background || '#F5F3EF'})` }} />
            </div>
          );
        })()}

      {/* Statistics Dashboard */}
      {stats && (
        <div className="mb-3 md:mb-6">
          <div className="flex items-center justify-between mb-2 md:mb-4">
            <h2 className="text-sm md:text-lg font-semibold" style={{ color: theme.text }}>Kitchen Statistics</h2>
            <button
              onClick={() => setIsStatsMinimized(!isStatsMinimized)}
              className="p-2 rounded-md transition-colors text-sm"
              style={{
                backgroundColor: theme.button.secondary.bg,
                color: theme.button.secondary.text
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = theme.button.secondary.hover;
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = theme.button.secondary.bg;
              }}
            >
              {isStatsMinimized ? '📊 Show Stats' : '➖ Hide Stats'}
            </button>
          </div>
          {!isStatsMinimized && (
            <div className="grid grid-cols-4 md:grid-cols-4 lg:grid-cols-7 gap-2 md:gap-4">
          <div className="p-2 md:p-4 rounded-lg border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components?.status?.pending?.text || '#f59e0b' }}>
            <h3 className="text-[10px] md:text-sm font-medium" style={{ color: theme.text }}>Pending</h3>
            <p className="text-lg md:text-2xl font-bold" style={{ color: theme.components?.status?.pending?.text || '#f59e0b' }}>{stats.pendingCount}</p>
          </div>
          <div className="p-2 md:p-4 rounded-lg border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components?.status?.accepted?.text || '#3b82f6' }}>
            <h3 className="text-[10px] md:text-sm font-medium" style={{ color: theme.text }}>Accepted</h3>
            <p className="text-lg md:text-2xl font-bold" style={{ color: theme.components?.status?.accepted?.text || '#3b82f6' }}>{stats.acceptedCount}</p>
          </div>
          <div className="p-2 md:p-4 rounded-lg border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components?.status?.preparing?.text || '#f59e0b' }}>
            <h3 className="text-[10px] md:text-sm font-medium" style={{ color: theme.text }}>Preparing</h3>
            <p className="text-lg md:text-2xl font-bold" style={{ color: theme.components?.status?.preparing?.text || '#f59e0b' }}>{stats.preparingCount}</p>
          </div>
          <div className="p-2 md:p-4 rounded-lg border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.components?.status?.ready?.text || '#10b981' }}>
            <h3 className="text-[10px] md:text-sm font-medium" style={{ color: theme.text }}>Ready</h3>
            <p className="text-lg md:text-2xl font-bold" style={{ color: theme.components?.status?.ready?.text || '#10b981' }}>{stats.readyCount}</p>
          </div>
          <div className="p-2 md:p-4 rounded-lg border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.primary }}>
            <h3 className="text-[10px] md:text-sm font-medium" style={{ color: theme.text }}>Active</h3>
            <p className="text-lg md:text-2xl font-bold" style={{ color: theme.primary }}>{stats.totalActiveCount}</p>
          </div>
          <div className="p-2 md:p-4 rounded-lg border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.success }}>
            <h3 className="text-[10px] md:text-sm font-medium" style={{ color: theme.text }}>Avg Prep</h3>
            <p className="text-lg md:text-xl font-bold" style={{ color: theme.success }}>{stats.avgPrepTimeMinutes}m</p>
          </div>
          <div className="p-2 md:p-4 rounded-lg border" style={{ backgroundColor: theme.components.card.background, borderColor: theme.accent }}>
            <h3 className="text-[10px] md:text-sm font-medium" style={{ color: theme.text }}>Done Today</h3>
            <p className="text-lg md:text-2xl font-bold" style={{ color: theme.accent }}>{stats.completedTodayCount}</p>
          </div>
            </div>
          )}
        </div>
      )}

      {/* Inline loading indicator — shows during filter transitions */}
      {isFetching && kitchenOrders.length > 0 && (
        <div className="flex items-center justify-center py-3">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full" style={{ backgroundColor: theme.components?.card?.background || '#fff', border: `1px solid ${theme.gray?.[200] || '#E5E5E3'}`, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <div className="animate-spin rounded-full h-4 w-4 border-2" style={{ borderColor: theme.gray?.[200] || '#E5E5E3', borderTopColor: theme.primary || 'var(--action)' }} />
            <span className="text-[11px] font-medium" style={{ color: theme.gray?.[500] || '#888' }}>Updating...</span>
          </div>
        </div>
      )}

      {/* Orders — Status Columns (horizontal scroll) */}
      {kitchenOrders.length > 0 ? (
        (() => {
          const visibleOrders = kitchenOrders.filter((order) => focusedKotId === null || order.kotId === focusedKotId);
          const statusOrder = ['PENDING', 'ACKNOWLEDGED', 'PREPARING', 'READY', 'SERVED'];
          const grouped = new Map<string, typeof visibleOrders>();
          for (const o of visibleOrders) {
            const s = o.kotStatus || 'PENDING';
            if (!grouped.has(s)) grouped.set(s, []);
            grouped.get(s)!.push(o);
          }
          const STATUS_HEADER_COLORS: Record<string, string> = {
            PENDING: '#D4845E',
            ACKNOWLEDGED: '#2D6CB5',
            PREPARING: '#2D6CB5',
            READY: '#1E8E5A',
            SERVED: '#2F7D5B',
          };

          return (
            <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2" style={{ minHeight: 300, WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 32px), transparent)', maskImage: 'linear-gradient(to right, black calc(100% - 32px), transparent)' }}>
              {statusOrder.filter(s => grouped.has(s)).map(status => {
                const orders = grouped.get(status)!;
                const headerColor = STATUS_HEADER_COLORS[status] || theme.gray?.[500] || '#888';
                return (
                  <div
                    key={status}
                    className="flex flex-col rounded-xl overflow-hidden"
                    style={{
                      // Fluid utilization: columns share the full viewport width;
                      // min-width keeps them readable and forces horizontal
                      // scroll only when there are genuinely too many columns.
                      flex: '1 1 360px',
                      minWidth: 320,
                      backgroundColor: theme.components?.card?.background || '#FFF',
                      border: `1px solid ${theme.components?.card?.border || '#E8E6E1'}`,
                    }}
                  >
                    {/* Colored column header */}
                    <div
                      className="px-4 py-2.5 flex items-center justify-between"
                      style={{ backgroundColor: headerColor, color: 'var(--text-inverse)' }}
                    >
                      <span className="text-sm font-bold uppercase tracking-wide">{status}</span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.25)' }}>
                        {orders.length}
                      </span>
                    </div>
                    {/* KOT cards — internal responsive grid: wide columns lay
                        cards 2-up+, so width AND height are used; narrow
                        columns fall back to a single stack. */}
                    <div className="flex-1 overflow-y-auto p-3" style={{ maxHeight: 'calc(100vh - 280px)', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12, alignContent: 'start' }}>
                      {orders.map((kitchenOrder) => {
            const statusColor = getStatusColor(kitchenOrder.kotStatus);
            const urgencyColor = getUrgencyColor(kitchenOrder.urgency);
            const nextStatus = getNextStatus(kitchenOrder.kotStatus);
            const isUpdating = updatingOrderId === kitchenOrder.kotId;

            return (
              <div
                key={kitchenOrder.kotId}
                className={`p-4 rounded-lg border-2 transition-all transform hover:scale-102 cursor-pointer ${
                  newKotIds.has(kitchenOrder.kotId) ? 'animate-pulse-border' : ''
                }`}
                style={{
                  backgroundColor: theme.components.card.background,
                  borderColor: statusColor.border,
                  boxShadow: newKotIds.has(kitchenOrder.kotId)
                    ? `0 0 30px ${theme.primary}, 0 0 60px ${theme.primary}40`
                    : `0 0 20px ${urgencyColor}22`,
                  animation: newKotIds.has(kitchenOrder.kotId) ? 'glow 1s ease-in-out infinite alternate' : 'none',
                  overflow: 'visible'
                }}
                onClick={() => openOrderModal(kitchenOrder)}
                data-kot-card
                data-kot-id={kitchenOrder.kotId}
              >
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-[15px] font-bold" style={{ color: theme.text, fontFamily: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", letterSpacing: 0, fontVariantNumeric: 'tabular-nums' }}>
                        {kitchenOrder.kotNumber}
                      </h3>
                      {newKotIds.has(kitchenOrder.kotId) && (
                        <span 
                          className="px-2 py-1 text-xs font-bold rounded-full animate-bounce"
                          style={{ 
                            backgroundColor: theme.success,
                            color: 'var(--text-inverse)',
                            boxShadow: `0 0 10px ${theme.success}`
                          }}
                        >
                          NEW
                        </span>
                      )}
                    </div>
                    <p className="text-sm" style={{ color: theme.gray[600] }}>
                      Order {formatForKitchen(kitchenOrder.order.customOrderId || `#${kitchenOrder.order.id}`)}
                      {kitchenOrder.tableInfo && kitchenOrder.tableInfo !== 'Unknown' && ` • ${kitchenOrder.tableInfo}`}
                    </p>
                    {kitchens.length > 1 && (
                      <p className="text-xs mt-1" style={{ color: theme.primary }}>
                        {kitchenOrder.kitchenName.replace(/\s*Kitchen\s*$/i, '')}
                      </p>
                    )}
                  </div>
                  <div
                    className="w-4 h-4 rounded-full animate-pulse"
                    style={{ backgroundColor: urgencyColor }}
                    title={`${kitchenOrder.urgency} urgency`}
                  ></div>
                </div>

                {/* Status Badge */}
                <div className="mb-3">
                  <span
                    className="px-3 py-1 text-sm font-medium rounded-full"
                    style={{
                      backgroundColor: statusColor.bg,
                      color: statusColor.text
                    }}
                  >
                    {kitchenOrder.kotStatus}
                  </span>
                  <span className="ml-2 text-sm" style={{ color: theme.gray[500] }}>
                    {kitchenOrder.timeAgo}
                  </span>
                </div>

                {/* Customer Info - Simplified */}
                <div className="mb-3">
                  <p className="text-sm" style={{ color: theme.text }}>
                    <span className="font-medium">Customer:</span> {
                      kitchenOrder.order.guestName?.split(' ')[0] || 
                      (kitchenOrder.order.user ? kitchenOrder.order.user.username.split(' ')[0] : 'Walk-in')
                    }
                  </p>
                  <p className="text-sm" style={{ color: theme.text }}>
                    <span className="font-medium">Items:</span> {kitchenOrder.itemCount}
                    <span className="ml-2 font-semibold">{CURRENCY.symbol}{kitchenOrder.total.toFixed(2)}</span>
                  </p>
                </div>

                {/* Order Items Preview */}
                <div className="mb-4">
                  <div className="space-y-2">
                    {kitchenOrder.order.items.slice(0, 3).map((item, index) => {
                      const itemStyle = isModernTheme ? getItemStyle(item.menuItemName || '') : null;
                      return (
                        <div key={index} className="text-sm">
                          <div className="flex items-center gap-2 flex-wrap">
                            {isModernTheme && itemStyle && (
                              <span 
                                className="w-6 h-6 rounded-md flex items-center justify-center text-sm flex-shrink-0"
                                style={{ background: itemStyle.gradient }}
                              >
                                {itemStyle.emoji}
                              </span>
                            )}
                            <span 
                              className="font-medium" 
                              style={{ color: isModernTheme && itemStyle ? itemStyle.color : theme.gray[700] }}
                            >
                              {item.quantity}x {item.menuItemName}
                            </span>
                            {item.portionName && (
                              <span
                                className="px-1.5 py-0.5 text-xs font-medium"
                                style={isModernTheme && itemStyle ? {
                                  backgroundColor: `${itemStyle.color}15`,
                                  color: itemStyle.color,
                                  borderRadius: '9999px'
                                } : {
                                  backgroundColor: theme.primary + '15',
                                  color: theme.primary,
                                  borderRadius: '4px'
                                }}
                              >
                                {item.portionName}
                              </span>
                            )}
                            {item.spiceLevel && (
                              <SpiceLevelBadge
                                spiceLevel={item.spiceLevel}
                                size="small"
                              />
                            )}
                            <DietaryDot type={kitchenOrder.order.itemDietary?.[String(item.menuItemId)] as 'VEG' | 'NON_VEG' | 'VEGAN' | undefined} />
                          </div>
                          {(kitchenOrder.order.itemAllergens?.[String(item.menuItemId)] || []).length > 0 && (
                            <span className="text-xs font-semibold block ml-2 mt-0.5 text-red-700">
                              ⚠ {(kitchenOrder.order.itemAllergens?.[String(item.menuItemId)] || []).map(a => ALLERGEN_LABELS[a] || a).join(', ')}
                            </span>
                          )}
                          {item.comment && !item.comment.match(/^Order placed at counter\s*\(Table:.*\)$/) && (
                            <span className="italic text-xs block ml-2 mt-0.5" style={{ color: theme.gray[500] }}>
                              Note: {item.comment.replace(/\s*\(Table:.*\)$/, '')}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {kitchenOrder.order.items.length > 3 && (
                      <div className="text-xs" style={{ color: theme.gray[500] }}>
                        +{kitchenOrder.order.items.length - 3} more items...
                      </div>
                    )}
                  </div>
                </div>

                {/* Allergy declaration (UK plan Phase 4) — compact strip below the items */}
                {kitchenOrder.order.allergyNote && (
                  <div
                    className="mb-3 px-2 py-1.5 rounded-md"
                    style={{
                      backgroundColor: 'rgba(239, 68, 68, 0.08)',
                      border: '1.5px solid rgba(239, 68, 68, 0.45)'
                    }}
                  >
                    <p className="text-xs font-semibold text-red-800">
                      ⚠️ <span className="font-bold uppercase text-red-700">Allergy:</span> {kitchenOrder.order.allergyNote}
                    </p>
                  </div>
                )}

                {/* Order-level Special Instructions */}
                {kitchenOrder.order.orderNotes && (
                  <div
                    className="mb-4 p-3 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(251, 191, 36, 0.1)',
                      border: '1px solid rgba(251, 191, 36, 0.3)'
                    }}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-amber-600 text-sm">📝</span>
                      <div>
                        <p className="text-xs font-semibold text-amber-700 mb-1">Special Instructions:</p>
                        <p className="text-sm text-amber-800">{kitchenOrder.order.orderNotes}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-2">
                  {/* Print KOT Button - Hide for stewards */}
                  {!isSteward && (
                    <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Close any open quick menu before opening print modal
                      setMarkAsOpenForKotId(null);
                      openKOTModal(kitchenOrder.order.id);
                    }}
                    className="sog-press w-full py-2 px-4 rounded-md font-medium transition-colors text-sm flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: theme.button.secondary.bg,
                      color: theme.button.secondary.text,
                      border: `1px solid ${theme.button.secondary.bg}`
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = theme.button.secondary.hover;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = theme.button.secondary.bg;
                    }}
                  >
                    🖨️ Print KOT
                    {kitchenOrder.printCount && kitchenOrder.printCount > 0 && (
                      <span className="text-xs px-2 py-0.5 rounded-full" 
                            style={{ backgroundColor: theme.success + '20', color: theme.success }}>
                        Printed {kitchenOrder.printCount}x
                      </span>
                    )}
                  </button>
                  )}

                  {/* Status Update Button */}
                  {/* For vendors: show normal status progression (hide for already served KOTs) */}
                  {!isSteward && kitchenOrder.kotStatus !== 'SERVED' && (
                    <div className="relative inline-block w-full">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                          const bottomSafeArea = 64; // account for bottom nav / safe area
                          const spaceBelow = window.innerHeight - r.bottom - bottomSafeArea;
                          const spaceAbove = r.top;
                          const desired = 240; // approx height of menu (2 rows)
                          let place: 'above' | 'below';
                          if (spaceBelow >= desired) place = 'below';
                          else if (spaceAbove >= desired) place = 'above';
                          else place = spaceBelow >= spaceAbove ? 'below' : 'above';
                          setMarkAsPlacement(place);
                          const available = place === 'below' ? spaceBelow - 8 : spaceAbove - 8;
                          setMarkAsMaxHeight(Math.max(160, Math.min(320, available)));
                          setMarkAsOpenForKotId((prev: number | null) => (prev === kitchenOrder.kotId ? null : kitchenOrder.kotId));
                        }}
                        className="sog-press w-full py-2 px-4 rounded-md font-medium transition-colors disabled:opacity-50 relative flex items-center justify-center"
                        style={{
                          backgroundColor: theme.button.primary.bg,
                          color: theme.button.primary.text
                        }}
                        onMouseOver={(e) => {
                          if (!isUpdating) {
                            e.currentTarget.style.backgroundColor = theme.button.primary.hover;
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!isUpdating) {
                            e.currentTarget.style.backgroundColor = theme.button.primary.bg;
                          }
                        }}
                        ref={(el) => (markAsTriggerRefs.current[kitchenOrder.kotId] = el)}
                        aria-expanded={markAsOpenForKotId === kitchenOrder.kotId}
                        aria-haspopup="menu"
                      >
                        Mark as ▾
                      </button>
                      {markAsOpenForKotId === kitchenOrder.kotId && createPortal(
                        <div
                          ref={(el) => (markAsMenuRef.current = el)}
                          className="fixed rounded-md shadow-lg border z-[200000] p-2"
                          style={{
                            left: markAsMenuLeft,
                            top: markAsMenuTop,
                            width: '256px',
                            backgroundColor: theme.components.card.background,
                            borderColor: theme.components.card.border,
                            maxWidth: '90vw'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="grid grid-cols-2 gap-2 overflow-auto" style={{ maxHeight: markAsMaxHeight }}>
                            {getValidNextKOTStatuses(kitchenOrder.kotStatus).map((status) => {
                              const ts = theme.components?.status;
                              const styles: Record<string, { bg: string; border: string; text: string; icon: string }> = {
                                ACKNOWLEDGED: { bg: ts?.accepted?.bg || '#DBEAFE', border: ts?.accepted?.border || '#3B82F6', text: ts?.accepted?.text || '#2563EB', icon: '✅' },
                                PREPARING: { bg: ts?.preparing?.bg || '#FED7AA', border: ts?.preparing?.border || '#FB923C', text: ts?.preparing?.text || '#EA580C', icon: '👨‍🍳' },
                                READY: { bg: ts?.ready?.bg || '#D1FAE5', border: ts?.ready?.border || '#10B981', text: ts?.ready?.text || '#059669', icon: '🔔' },
                                SERVED: { bg: ts?.served?.bg || '#E5E7EB', border: ts?.served?.border || '#9CA3AF', text: ts?.served?.text || '#374151', icon: '🍽️' },
                              };
                              const s = styles[status] || styles.ACKNOWLEDGED;
                              const isAck = status === 'ACKNOWLEDGED';
                              return (
                                <button
                                  key={status}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMarkAsOpenForKotId(null);
                                    updateKOTStatus(kitchenOrder.kotId, status);
                                  }}
                                  className={`p-2 rounded-lg border text-left transition-all ${isAck ? 'px-1' : ''}`}
                                  style={{ backgroundColor: s.bg, borderColor: s.border }}
                                >
                                  <div className={`flex items-center ${isAck ? 'gap-1 text-[10px]' : 'gap-1.5 text-xs'} font-semibold`} style={{ color: s.text }}>
                                    <span className="shrink-0">{s.icon}</span>
                                    <span className="whitespace-nowrap">{status.replace('_', ' ')}</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>,
                        document.body
                      )}
                    </div>
                  )}
                  
                  {/* For stewards: only show "Mark as Served" button when order is ready */}
                  {isSteward && kitchenOrder.kotStatus === 'READY' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateKOTStatus(kitchenOrder.kotId, 'SERVED');
                      }}
                      disabled={updatingOrderId === kitchenOrder.kotId}
                      className="w-full py-2 px-4 rounded-md font-medium transition-colors disabled:opacity-50"
                      style={{
                        backgroundColor: theme.button.primary.bg,
                        color: theme.button.primary.text
                      }}
                      onMouseOver={(e) => {
                        if (updatingOrderId !== kitchenOrder.kotId) {
                          e.currentTarget.style.backgroundColor = theme.button.primary.hover;
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = theme.button.primary.bg;
                      }}
                    >
                      {updatingOrderId === kitchenOrder.kotId ? 'Updating...' : '✓ Mark as Served'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()
      ) : (
        <div className="text-center py-12">
          <CheckCircleIcon style={{ width: 56, height: 56, color: 'var(--status-ok)', margin: '0 auto 16px' }} />
          <h2 className="text-2xl font-bold mb-2" style={{ color: theme.text }}>
            All caught up!
          </h2>
          <p style={{ color: theme.gray[600] }}>
            {statusFilter 
              ? `No ${statusFilter.toLowerCase()} orders for ${dateFilter === 'today' ? 'today' : dateFilter}`
              : `No orders for ${dateFilter === 'today' ? 'today' : dateFilter}`}
          </p>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-6 sm:mt-8 px-4">
          {/* Page Info */}
          <div className="text-center mb-4 text-sm font-medium" style={{ color: theme.gray[700] }}>
            Showing <span className="font-bold" style={{ color: theme.primary }}>{currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalElements)}</span> of <span className="font-bold" style={{ color: theme.primary }}>{totalElements}</span> orders
          </div>

          {/* Enhanced Pagination */}
          <div className="flex items-center justify-center gap-3 max-w-lg mx-auto">
            {/* Previous Button */}
            <button
              onClick={() => {
                if (currentPage > 0) {
                  fetchKitchenOrders(currentPage - 1);
                }
              }}
              disabled={currentPage === 0 || loading}
              className="flex items-center justify-center px-5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                backgroundColor: currentPage === 0 ? theme.gray[200] : theme.components.card.background,
                color: currentPage === 0 ? theme.gray[400] : theme.primary,
                border: `1px solid ${currentPage === 0 ? theme.gray[300] : theme.primary}`,
                minHeight: 44,
              }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Previous</span>
            </button>

            {/* Current Page Indicator */}
            <div
              className="px-5 py-2.5 rounded-lg font-semibold text-sm min-w-[110px] text-center"
              style={{
                backgroundColor: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-default)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {currentPage + 1} <span style={{ color: 'var(--text-tertiary)' }}>of</span> {totalPages}
            </div>

            {/* Next Button */}
            <button
              onClick={() => {
                if (currentPage < totalPages - 1) {
                  fetchKitchenOrders(currentPage + 1);
                }
              }}
              disabled={currentPage >= totalPages - 1 || loading}
              className="flex items-center justify-center px-5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                backgroundColor: currentPage >= totalPages - 1 ? theme.gray[200] : theme.components.card.background,
                color: currentPage >= totalPages - 1 ? theme.gray[400] : theme.primary,
                border: `1px solid ${currentPage >= totalPages - 1 ? theme.gray[300] : theme.primary}`,
                minHeight: 44,
              }}
            >
              <span className="hidden sm:inline">Next</span>
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Quick Jump (Optional - for better UX) */}
          {totalPages > 3 && (
            <div className="flex items-center justify-center gap-2 mt-4 text-sm">
              <span style={{ color: theme.gray[600] }}>Jump to:</span>
              <button
                onClick={() => fetchKitchenOrders(0)}
                disabled={currentPage === 0}
                className="px-3 py-1.5 rounded-lg font-medium transition-all hover:scale-105 disabled:opacity-40"
                style={{
                  backgroundColor: currentPage === 0 ? theme.primary + '22' : 'transparent',
                  color: theme.primary,
                  border: `1.5px solid ${theme.primary}`,
                }}
              >
                First
              </button>
              <button
                onClick={() => fetchKitchenOrders(totalPages - 1)}
                disabled={currentPage === totalPages - 1}
                className="px-3 py-1.5 rounded-lg font-medium transition-all hover:scale-105 disabled:opacity-40"
                style={{
                  backgroundColor: currentPage === totalPages - 1 ? theme.primary + '22' : 'transparent',
                  color: theme.primary,
                  border: `1.5px solid ${theme.primary}`,
                }}
              >
                Last
              </button>
            </div>
          )}
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: theme.components.card.background }}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-inherit p-6 border-b border-gray-200 flex justify-between items-center rounded-t-2xl">
              <h2 className="text-xl font-bold" style={{ color: theme.primary }}>
                Order {formatForKitchen(selectedOrder.order.customOrderId || `#${selectedOrder.order.id}`)} Details
              </h2>
              <button
                onClick={closeOrderModal}
                className="p-2 rounded-full transition-colors"
                style={{
                  backgroundColor: theme.button.secondary.bg,
                  color: theme.button.secondary.text
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = theme.button.secondary.hover;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = theme.button.secondary.bg;
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Order Summary */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>Customer</p>
                    <p className="text-sm" style={{ color: theme.gray[600] }}>
                      {
                        selectedOrder.order.guestName?.split(' ')[0] || 
                        (selectedOrder.order.user ? selectedOrder.order.user.username.split(' ')[0] : 'Walk-in')
                      }
                    </p>
                  </div>
                  {selectedOrder.tableInfo && selectedOrder.tableInfo !== 'Unknown' && (
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>Table</p>
                    <p className="text-sm" style={{ color: theme.gray[600] }}>
                      {selectedOrder.tableInfo}
                    </p>
                  </div>
                  )}
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>Order Time</p>
                    <p className="text-sm" style={{ color: theme.gray[600] }}>
                      {new Date(selectedOrder.order.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: theme.text }}>Total</p>
                    <p className="text-lg font-bold" style={{ color: theme.success }}>
                      {CURRENCY.symbol}{selectedOrder.total.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Allergy declaration — always highlighted for the kitchen */}
              {selectedOrder.order.allergyNote && (
                <div
                  className="mb-4 p-3 rounded-lg"
                  style={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    border: '2px solid rgba(239, 68, 68, 0.5)'
                  }}
                >
                  <div className="flex items-start gap-2">
                    <span className="text-red-600 text-sm">⚠️</span>
                    <div>
                      <p className="text-xs font-bold text-red-700 mb-1 uppercase">Allergy — read before preparing:</p>
                      <p className="text-sm font-semibold text-red-800">{selectedOrder.order.allergyNote}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Detailed Items */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: theme.primary }}>
                  Order Items
                </h3>
                <div className="space-y-3">
                  {selectedOrder.order.items.map((item, index) => {
                    const itemStyle = isModernTheme ? getItemStyle(item.menuItemName || '') : null;
                    return (
                      <div
                        key={index}
                        className="p-3 border rounded-lg"
                        style={isModernTheme && itemStyle ? {
                          backgroundColor: `${itemStyle.color}08`,
                          borderColor: `${itemStyle.color}30`,
                          borderWidth: '1.5px'
                        } : {
                          backgroundColor: theme.components.card.background,
                          borderColor: theme.components.card.border
                        }}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              {isModernTheme && itemStyle && (
                                <span 
                                  className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                                  style={{ background: itemStyle.gradient }}
                                >
                                  {itemStyle.emoji}
                                </span>
                              )}
                              <h4 
                                className="font-semibold text-lg" 
                                style={{ color: isModernTheme && itemStyle ? itemStyle.color : theme.text }}
                              >
                                {item.quantity}× {item.menuItemName}
                              </h4>
                              {item.portionName && (
                                <span
                                  className="px-2 py-0.5 text-xs font-medium rounded-full"
                                  style={isModernTheme && itemStyle ? {
                                    backgroundColor: `${itemStyle.color}20`,
                                    color: itemStyle.color
                                  } : {
                                    backgroundColor: theme.primary + '20',
                                    color: theme.primary
                                  }}
                                >
                                  {item.portionName}
                                </span>
                              )}
                              {item.spiceLevel && (
                                <SpiceLevelBadge
                                  spiceLevel={item.spiceLevel}
                                  size="small"
                                />
                              )}
                              <DietaryDot type={selectedOrder.order.itemDietary?.[String(item.menuItemId)] as 'VEG' | 'NON_VEG' | 'VEGAN' | undefined} />
                            </div>
                            {(selectedOrder.order.itemAllergens?.[String(item.menuItemId)] || []).length > 0 && (
                              <p className="text-xs font-semibold mt-1 text-red-700">
                                ⚠ Contains: {(selectedOrder.order.itemAllergens?.[String(item.menuItemId)] || []).map(a => ALLERGEN_LABELS[a] || a).join(', ')}
                              </p>
                            )}
                            <div
                              className="text-sm mt-1"
                              style={{ color: isModernTheme && itemStyle ? itemStyle.color : theme.gray[600] }}
                            >
                              <span>{CURRENCY.symbol}{(item.menuItem?.price || item.price || 0).toFixed(2)} each</span>
                              {item.quantity > 1 && (
                                <span className="ml-2">× {item.quantity} = {CURRENCY.symbol}{((item.menuItem?.price || item.price || 0) * item.quantity).toFixed(2)}</span>
                              )}
                            </div>
                            {item.toppings && item.toppings.length > 0 && (
                              <div className="mt-2 pl-3" style={{ borderLeft: `2px solid ${isModernTheme && itemStyle ? itemStyle.color : theme.primary}40` }}>
                                <p className="text-xs font-medium" style={{ color: theme.gray[600] }}>
                                  Toppings:
                                </p>
                                <ul className="mt-1 space-y-0.5">
                                  {item.toppings.map((topping, idx) => (
                                    <li key={idx} className="text-xs flex items-center gap-1" style={{ color: theme.gray[500] }}>
                                      <span style={{ color: isModernTheme && itemStyle ? itemStyle.color : theme.primary }}>•</span>
                                      {topping.name}
                                      {topping.price && topping.price > 0 && (
                                        <span className="text-xs" style={{ color: theme.gray[400] }}>
                                          (+{CURRENCY.symbol}{topping.price.toFixed(2)})
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          {item.comment && !item.comment.match(/^Order placed at counter\s*\(Table:.*\)$/) && (
                            <div className="mt-2 p-2 rounded" style={{ backgroundColor: 'var(--status-warn-bg)', border: '1px solid var(--status-warn)' }}>
                              <p className="text-xs font-medium" style={{ color: 'var(--status-warn-text)' }}>
                                Special Instructions:
                              </p>
                              <p className="text-xs italic mt-0.5" style={{ color: 'var(--status-warn-text)' }}>
                                {item.comment.replace(/\s*\(Table:.*\)$/, '')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Status Update */}
              {/* Status Update Section - hide for already served KOTs */}
              {selectedOrder.kotStatus !== 'SERVED' && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold mb-4" style={{ color: theme.primary }}>
                  Update Status
                </h3>
                <div className="flex flex-wrap gap-2">
                  {/* For vendors: show all status options */}
                  {!isSteward && ['ACKNOWLEDGED', 'PREPARING', 'READY', 'SERVED'].map((status) => (
                    <button
                      key={status}
                      onClick={() => updateKOTStatus(selectedOrder.kotId, status)}
                      disabled={updatingOrderId === selectedOrder.kotId}
                      className="px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                      style={{
                        backgroundColor: selectedOrder.kotStatus === status ? theme.button.primary.bg : theme.button.secondary.bg,
                        color: selectedOrder.kotStatus === status ? theme.button.primary.text : theme.button.secondary.text,
                        border: `1px solid ${selectedOrder.kotStatus === status ? theme.button.primary.bg : theme.button.secondary.bg}`
                      }}
                      onMouseOver={(e) => {
                        if (updatingOrderId !== selectedOrder.kotId && selectedOrder.kotStatus !== status) {
                          e.currentTarget.style.backgroundColor = theme.button.secondary.hover;
                        }
                      }}
                      onMouseOut={(e) => {
                        if (updatingOrderId !== selectedOrder.kotId) {
                          e.currentTarget.style.backgroundColor = selectedOrder.kotStatus === status ? theme.button.primary.bg : theme.button.secondary.bg;
                        }
                      }}
                    >
                      {status}
                    </button>
                  ))}
                  
                  {/* For stewards: only show "Mark as Served" button when order is ready */}
                  {isSteward && selectedOrder.kotStatus === 'READY' && (
                    <button
                      onClick={() => updateKOTStatus(selectedOrder.kotId, 'SERVED')}
                      disabled={updatingOrderId === selectedOrder.kotId}
                      className="px-4 py-2 rounded-md font-medium transition-colors disabled:opacity-50"
                      style={{
                        backgroundColor: theme.button.primary.bg,
                        color: theme.button.primary.text,
                      }}
                      onMouseOver={(e) => {
                        if (updatingOrderId !== selectedOrder.kotId) {
                          e.currentTarget.style.backgroundColor = theme.button.primary.hover;
                        }
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = theme.button.primary.bg;
                      }}
                    >
                      {updatingOrderId === selectedOrder.kotId ? 'Updating...' : '✓ Mark as Served'}
                    </button>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KOT Print Modal - Hide for stewards */}
      {!isSteward && (
        <KOTPrintModal
          isOpen={showKOTModal}
          onClose={closeKOTModal}
          orderId={kotOrderId}
          autoPrint={shouldAutoPrint}
        />
      )}
      
      
      {/* Auto-print Notification */}
      {autoPrintNotification && (
        <AutoPrintNotification
          kotNumber={autoPrintNotification.kotNumber}
          tableInfo={autoPrintNotification.tableInfo}
          itemCount={autoPrintNotification.itemCount}
          onClose={() => setAutoPrintNotification(null)}
        />
      )}
    </div>
  );
};

export default KitchenDisplayPage;